/**
 * Student mappings view (ISS.db) for inspecting local DB mappings.
 * Admins can trigger a DB refresh workflow from WELA and Cypher sources.
 */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Button,
  Modal,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { studentMappingService, StudentMapping, adminService } from '../services/api.service';
import { useAuth } from '../context/AuthContext';

/**
 * Displays searchable mapping table and drives the update modal flow.
 */
const StudentMappings: React.FC = () => {
  const { user } = useAuth();
  const [mappings, setMappings] = useState<StudentMapping[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isUpdateModalOpen, setUpdateModalOpen] = useState<boolean>(false);
  const [updateInProgress, setUpdateInProgress] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [dbLastUpdated, setDbLastUpdated] = useState<string | null>(null);
  
  const updateSteps = [
      'Starting student DB update process...',
      'Clearing existing student tables...',
      'Populating WELA students...',
      'Populating Cypher (NeoLMS) students...',
      'Update finished successfully.',
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const mappingsData = await studentMappingService.getStudentMappings(searchTerm);
      setMappings(mappingsData);
      
      const dbLastUpdatedData = await adminService.getDbLastUpdated();
      if (dbLastUpdatedData.last_updated) {
        const formattedDate = new Date(dbLastUpdatedData.last_updated).toLocaleString();
        setDbLastUpdated(formattedDate);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchData();
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateDatabase = async () => {
    setUpdateModalOpen(true);
    setUpdateInProgress(true);
    setUpdateError(null);
    setUpdateSuccess(null);
    setActiveStep(0);

    const stepInterval = setInterval(() => {
        setActiveStep((prevActiveStep: number) => {
            if (prevActiveStep < updateSteps.length - 2) {
                return prevActiveStep + 1;
            }
            return prevActiveStep;
        });
    }, 3000);

    try {
      const response = await adminService.updateStudentDb();
      clearInterval(stepInterval);
      setActiveStep(updateSteps.length - 1);
      setUpdateSuccess(response.message);
      fetchData(); // Re-fetch data after successful update
    } catch (err: any) {
      clearInterval(stepInterval);
      setUpdateError(err.message || 'An unexpected error occurred.');
    } finally {
      setUpdateInProgress(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Student Mappings (ISS.db)
        </Typography>
        
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flex: '1 1 100%' }}>
              Student Details
            </Typography>
            {dbLastUpdated && (
                <Typography variant="caption" sx={{ mr: 2, fontStyle: 'italic' }}>
                    DB Last updated: {dbLastUpdated}
                </Typography>
            )}
            {user?.is_admin && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateDatabase}
                  disabled={updateInProgress}
                  sx={{ mr: 2 }}
                >
                  Update Student DB
                </Button>
            )}
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search by name, WELA ID, or LMS ID"
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{ width: 350 }}
            />
          </Toolbar>
          
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
              <CircularProgress />
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && (
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>LMS ID</TableCell>
                    <TableCell>WELA ID</TableCell>
                    <TableCell>Full Name</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell>Grade Level</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.length > 0 ? (
                    mappings.map((mapping: StudentMapping) => (
                      <TableRow hover key={mapping.lms_id}>
                        <TableCell>{mapping.lms_id}</TableCell>
                        <TableCell>{mapping.wela_id}</TableCell>
                        <TableCell>{mapping.full_name}</TableCell>
                        <TableCell>{mapping.gender}</TableCell>
                        <TableCell>{mapping.grade_level}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No student mappings found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      <Modal
        open={isUpdateModalOpen}
        onClose={() => !updateInProgress && setUpdateModalOpen(false)}
        aria-labelledby="update-db-modal-title"
        aria-describedby="update-db-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="update-db-modal-title" variant="h6" component="h2">
            Student Database Update
          </Typography>
          <Box sx={{ width: '100%', mt: 3 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {updateSteps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    optional={
                      index === activeStep && updateInProgress ? (
                        <CircularProgress size={20} />
                      ) : null
                    }
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          {updateSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>{updateSuccess}</Alert>
          )}
          {updateError && (
            <Alert severity="error" sx={{ mt: 2 }}>{updateError}</Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={() => setUpdateModalOpen(false)} disabled={updateInProgress}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

    </Container>
  );
};

export default StudentMappings;
